import * as Misskey from "calckey-js";
import {
    markRaw,
    MaybeRefOrGetter,
    onUnmounted,
    onWatcherCleanup,
    readonly,
    ref,
    shallowRef,
    toValue,
    watch,
} from "vue";
import { MagEventChannel, types } from "magnetar-common";
import { injectLocal, MaybeRef } from "@vueuse/core";
import EventEmitter from "eventemitter3";
import { ShallowRef, unref } from "@vue/runtime-core";

export const useLegacyStream = () => {
    return injectLocal("legacyStream") as Readonly<
        ShallowRef<Misskey.Stream | null>
    >;
};

export const useLegacyStreamChannel = <C extends keyof Misskey.Channels>(
    channel: C,
    params?: Misskey.Channels[C]["params"],
    name?: string,
) => {
    const stream = useLegacyStream();

    const initialHandler =
        stream.value?.useChannel(channel, params, name) ?? null;
    const handler = shallowRef(initialHandler ? markRaw(initialHandler) : null);

    watch(stream, (newStream) => {
        handler.value = newStream
            ? markRaw(newStream.useChannel(channel, params, name))
            : null;
    });

    onUnmounted(() => {
        handler.value?.dispose();
    });

    return readonly(handler);
};

export const useStream = () => {
    return injectLocal("stream") as Readonly<
        ShallowRef<MagEventChannel | null>
    >;
};

export const useStreamFiltered = <T extends types.ChannelEvent["type"]>(
    messageType: T,
    listener: (val: (types.ChannelEvent & { type: T })["body"]) => void,
) => {
    const stream = useStream();

    const initialHandler =
        stream.value?.useFiltered(messageType, listener) ?? null;
    const handler = shallowRef(initialHandler ? markRaw(initialHandler) : null);

    watch(stream, (newStream) => {
        handler.value = newStream
            ? markRaw(newStream.useFiltered(messageType, listener))
            : null;
    });

    onUnmounted(() => {
        if (handler.value) {
            stream.value?.off("message", handler.value);
        }
    });

    return handler;
};

export const useEventEmitter = <
    T extends EventEmitter.EventNames<E>,
    E extends EventEmitter.ValidEventTypes = string | symbol,
>(
    eventSourceRef: MaybeRef<EventEmitter<E> | undefined | null>,
    eventKindRef: MaybeRefOrGetter<T>,
    fnRef: MaybeRef<EventEmitter.EventListener<E, T>>,
    once: boolean = false,
) => {
    const handler = shallowRef<EventEmitter.EventListener<E, T> | null>(null);
    const eventNamePrev = ref<T | null>(null);

    let stopWatch = () => {};
    const { stop } = watch(
        () => [unref(fnRef), toValue(eventKindRef), unref(eventSourceRef)],
        () => {
            const eventSource = unref(eventSourceRef);
            const handlerFn = unref(fnRef);
            const eventName = toValue(eventKindRef);

            if (!eventSource) {
                handler.value = null;
                return;
            }

            eventNamePrev.value = eventName;

            if (once) {
                eventSource.once(eventName, handlerFn);
                handler.value = handlerFn;
                stopWatch();
            } else {
                eventSource.on(eventName, handlerFn);
                handler.value = handlerFn;
            }

            onWatcherCleanup(() => {
                eventSource.off(eventName, handlerFn);
            });
        },
        {
            immediate: true,
        },
    );

    stopWatch = stop;

    onUnmounted(() => {
        stop();

        if (handler.value) {
            const eventSource = unref(eventSourceRef);
            eventSource?.off(toValue(eventKindRef), handler.value);
            handler.value = null;
        }
    });

    return { handler: readonly(handler), stop };
};
